(ns portal.social-monitoring.view
  (:require ["@mui/material/Chip$default" :as Chip]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium]]
            [cljs-bean.core :refer [->clj]]
            [common.queries :refer [use-query]]
            [common.resource.query.adset :refer [adsets-query]]
            [common.resource.query.advertiser :refer [search-integration-errors-query]]
            [common.resource.query.campaign :refer [campaigns-query]]
            [common.util :refer [spread-obj]]
            [common.widgets.data-grid :as dg]
            [common.widgets.page :as page]
            [manage.adset-table.view :refer [format-name]]
            [mate.core :as mc]
            [reitit.frontend.easy :as rfe]
            [uix.core :refer [$ defhook defui] :as uix]
            [uix.re-frame :refer [use-subscribe]]))

(defn- meta-adsets-select [^js data]
  (->> (->clj data)
       (filter (comp (partial = "meta") :channel))))

(defn- adset-options-select [^js data]
  (->> (meta-adsets-select data)
       (remove :archived)
       (mapv (fn [{:keys [name id]}]
               {:label name
                :value (str id)}))))

(defhook use-meta-adset-options []
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {adsets :data} (use-query (spread-obj (adsets-query {:advertiser-id advertiser-id})
                                              #js {:select adset-options-select}))]
    adsets))

(defn adset-id->adset-select [^js data]
  (->> (meta-adsets-select data)
       (mc/index-by :id)))

(defhook use-adset-id->adset [{:keys [adset-id]}]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])

        {adset-id->adset-name :data} (use-query (spread-obj (adsets-query {:advertiser-id advertiser-id})
                                                            #js {:select adset-id->adset-select}))]
    (when (seq adset-id->adset-name)
      (adset-id->adset-name adset-id))))

(defn- campaign-options-select [^js data]
  (->> (->clj data)
       (filter (comp seq :adset2Ids))
       (remove :archived)
       (mapv (fn [{:keys [name adset2Ids]}]
               {:label name
                :value (mapv str adset2Ids)}))))

(defhook use-campaign-options []
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {campaigns :data} (use-query (spread-obj (campaigns-query {:advertiser-id advertiser-id})
                                                 #js {:select campaign-options-select}))]
    campaigns))

(defhook use-adset-id->campaign-name [{:keys [adset-id]}]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])

        select (uix/use-callback (fn [^js data]
                                   (->> (->clj data)
                                        (some (fn [{:keys [adset2Ids name]}]
                                                (when (contains? (set adset2Ids) adset-id)
                                                  name)))))
                                 [adset-id])
        {name :data} (use-query (spread-obj (campaigns-query {:advertiser-id advertiser-id})
                                            #js {:select select}))]
    name))

(defhook use-adset-id->url-context-fn []
  (let [{:keys [advertiser-id agency-id]} (use-subscribe [:url-context])

        {campaigns :data} (use-query (campaigns-query {:advertiser-id advertiser-id}))]
    (uix/use-callback
     (fn [adset-id]
       {:agency-id agency-id
        :advertiser-id advertiser-id
        :campaign-id (some (fn [{:keys [adset2Ids id]}]
                             (when (contains? (set adset2Ids) adset-id)
                               id))
                           campaigns)
        :adset-id adset-id})
     [advertiser-id agency-id campaigns])))

(defui adset-cell [{adset-id :value}]
  (let [{:keys [name archived]} (use-adset-id->adset {:adset-id adset-id})]
    ($ format-name {:name name :archived archived})))

(defui campaign-cell [{adset-id :value}]
  (let [campaign-name (use-adset-id->campaign-name {:adset-id adset-id})]
    campaign-name))

(defui error-type-cell [{error-type :value}]
  (when error-type
    ($ Chip
       {:label error-type
        :size "small"
        :color "error"
        :variant "outlined"})))

(def columns-data
  #js [#js {:field "campaign-id"
            :headerName "Campaign"
            :sortable false
            :flex 1
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:adset-id row))
            :renderCell (dg/get-cell-renderer campaign-cell)}

       #js {:field "adset-id"
            :headerName "Adset"
            :sortable false
            :flex 2
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:adset-id row))
            :renderCell (dg/get-cell-renderer adset-cell)}


       #js {:field "facebook-adset-id"
            :headerName "Meta Adset ID"
            :sortable false
            :flex 1
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:facebook-adset-id row))}

       #js {:field "published-facebook-ad-id"
            :headerName "Meta Ad ID"
            :sortable false
            :flex 1
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:published-facebook-ad-id row))}

       #js {:field "error-timestamp"
            :headerName "Push/pull date"
            :type "dateTime"
            :flex 1
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (some-> (:error-timestamp row) js/Date.))}

       #js {:field "error-type"
            :headerName "Error type"
            :sortable false
            :flex 1
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:error-type row))
            :renderCell (dg/get-cell-renderer error-type-cell)}

       #js {:field "error-message"
            :headerName "Error message"
            :sortable false
            :flex 3
            :minWidth 300
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:error-message row))}])

(def field->sort-field
  {"error-timestamp" "error-timestamp>"})

(def field->resolver
  {"error-timestamp" "error-timestamp"
   "adset-id" "adset-id"
   "facebook-adset-id" "facebook-adset-id"
   "published-facebook-ad-id" "published-facebook-ad-id"
   "error-type" "error-type"
   "error-message" "error-message"})

(def query-filter-params
  {:field->sort-field field->sort-field
   :field->resolver field->resolver
   :default-sorting {:field "error-timestamp>" :sort "desc"}})

(defui adset-filter []
  (let [options (use-meta-adset-options)]
    ($ dg/quick-select-filter
       {:label "Adset(s)"
        :field "adset-id"
        :id "adsets-id"
        :data-testid "adset-filter-select"
        :filter-options options})))

(defui campaign-filter []
  (let [options (use-campaign-options)]
    ($ dg/quick-select-filter
       {:label "Campaign(s)"
        :field "adset-id"
        :id "adset-id"
        :data-testid "campaign-filter-select"
        :filter-options options})))

(defui datetime-filter []
  ($ dg/daterange-filter
     {:label "Daterange"
      :disableFuture true
      :field "error-timestamp"
      :id "error-timestamp"}))

(def Toolbar
  (uix/as-react
   (fn []
     ($ dg/grid-toolbar {:hide-quick-filter true
                         :sx #js {:justifyContent "flex-start"}}
        ($ campaign-filter)
        ($ adset-filter)
        ($ datetime-filter)))))

(defui advertiser-monitoring
  []
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        adset-id->url-context (use-adset-id->url-context-fn)
        initial-pagination-model #js {:page 0 :pageSize 25}
        initial-sort-model #js []

        table-query-fn (uix/use-callback
                        (fn [table-models]
                          (search-integration-errors-query
                           {:advertiser-id advertiser-id}
                           query-filter-params
                           table-models))
                        [advertiser-id])

        server-table-props (dg/use-server-table-props
                            {:initial-pagination-model initial-pagination-model
                             :initial-sort-model initial-sort-model
                             :table-query-fn table-query-fn
                             :prevent-loading-on-refetch true
                             :get-row-count-fn :total})

        get-row-id (uix/use-callback (fn [row]
                                       (str (:adset-id row) "-" (:error-timestamp row)))
                                     [])]
    ($ DataGridPremium
       (merge server-table-props
              {:columns columns-data
               :getRowId get-row-id
               :disableRowSelectionOnClick true
               :slots #js {:toolbar Toolbar
                           :cell dg/WithLinkCell}
               :slotProps #js {:cell #js {:href-fn (fn [row]
                                                     (rfe/href :adset/ad-variants (adset-id->url-context (:adset-id row)) nil "strategy"))}}
               :data-testid "integration-errors-table"
               :sx #js {:maxHeight "70rem"}}))))

(defui social-monitoring-page []
  ($ page/page
     ($ page/page-header {:title "Social monitoring"})
     ($ page/content-padding
        {:sx #js {:overflow "hidden"}}
        ($ advertiser-monitoring))))
